import { useAuth } from '@/lib/auth'
import { readOverrides } from '@/lib/debug'
import { logger } from '@/ui'
import { axiosInstance } from '@jobilla/nexus-sdk'
import { type AxiosError, CanceledError, type InternalAxiosRequestConfig } from 'axios'
import i18next from 'i18next'
import { type ReactNode, useEffect, useRef } from 'react'

// Set defaults immediately so they're available before any useEffect runs
axiosInstance.defaults.baseURL = import.meta.env.VITE_NEXUS_API_URL
axiosInstance.defaults.withCredentials = true
axiosInstance.defaults.headers.common.Accept = 'application/json'
axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
axiosInstance.defaults.timeout = 60_000

function buildAcceptLanguage(locale: string): string {
	const primary = locale.split('-')[0]
	// e.g. "fi-FI,fi;q=0.9,en;q=0.8" — gives the server the full locale, the
	// primary subtag as a fallback, then English as last resort.
	if (primary === 'en') {
		return `${locale}`
	}
	return `${locale},${primary};q=0.9,en;q=0.8`
}

if (i18next.language) {
	axiosInstance.defaults.headers.common['Accept-Language'] = buildAcceptLanguage(i18next.language)
}
i18next.on('languageChanged', (lng) => {
	axiosInstance.defaults.headers.common['Accept-Language'] = buildAcceptLanguage(lng)
})

const PUBLIC_PATHS = [
	'/auth/login',
	'/auth/register',
	'/auth/verify-registration',
	'/auth/refresh',
	'/auth/logout',
	'/auth/sso/google',
	'/auth/sso/callback',
	'/auth/switch-organisation',
	'/auth/select-organisation',
	'/auth/forgot-password',
	'/auth/reset-password',
	'/auth/verify-email',
	'/auth/set-password-by-invitation',
	'/auth/accept-invitation',
	'/interview-scheduling/booking/',
]

// @TODO: refactor to support dynamic path segments cleanly (e.g. /organisations/invitations/:token/peek)
function isPublicPath(url: string | undefined): boolean {
	if (!url) { return false }
	if (PUBLIC_PATHS.some((p) => url.startsWith(p))) { return true }
	return url.startsWith('/organisations/invitations/') && url.endsWith('/peek')
}

export function NexusAuthInterceptorProvider({ children }: { children: ReactNode }) {
	const { getAccessToken, refreshSession } = useAuth()
	const refreshPromiseRef = useRef<Promise<void> | null>(null)

	useEffect(() => {
		const requestId = axiosInstance.interceptors.request.use(async (config) => {
			const token = await getAccessToken()
			if (token) {
				config.headers.Authorization = `Bearer ${token}`
			} else if (!isPublicPath(config.url)) {
				throw new CanceledError('No access token available')
			}

			const overrides = readOverrides()
			if (Object.keys(overrides).length > 0) {
				config.headers['X-Debug-Overrides'] = JSON.stringify(overrides)
			}

			return config
		})

		const responseId = axiosInstance.interceptors.response.use(
			(response) => response,
			async (error: AxiosError) => {
				const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

				if (
					error.response?.status !== 401
					|| !originalRequest
					|| originalRequest._retry
					|| isPublicPath(originalRequest.url)
				) {
					return Promise.reject(error)
				}

				originalRequest._retry = true

				// Deduplicate concurrent refresh calls
				if (!refreshPromiseRef.current) {
					refreshPromiseRef.current = refreshSession().finally(() => {
						refreshPromiseRef.current = null
					})
				}

				try {
					logger.info('401 refresh started', { category: 'auth', data: { url: originalRequest.url } })
					await refreshPromiseRef.current
					logger.info('401 refresh succeeded', { category: 'auth' })
				} catch (refreshError) {
					logger.warn('401 refresh failed, rejecting request', {
						category: 'auth',
						data: {
							url: originalRequest.url,
							error: refreshError instanceof Error ? refreshError.message : String(refreshError),
						},
					})
					return Promise.reject(error)
				}

				const token = await getAccessToken()
				if (!token) {
					return Promise.reject(error)
				}

				originalRequest.headers.Authorization = `Bearer ${token}`
				return axiosInstance.request(originalRequest)
			},
		)

		return () => {
			axiosInstance.interceptors.request.eject(requestId)
			axiosInstance.interceptors.response.eject(responseId)
		}
	}, [getAccessToken, refreshSession])

	return <>{children}</>
}
